/**
 * Voice Memo Settings Panel Component
 * Controls auto-transcribe & analyze defaults
 */

import { useState } from 'react';
import { Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface VoiceMemoSettings {
  autoTranscribe: boolean;
  autoAnalyze: boolean;
  autoTranscribeOnUpload: boolean;
  autoAnalyzeOnUpload: boolean;
  autoTranscribeOnRecord: boolean;
  autoAnalyzeOnRecord: boolean;
}

interface VoiceMemoSettingsPanelProps {
  settings: VoiceMemoSettings;
  onSettingsChange: (settings: VoiceMemoSettings) => void;
}

export function VoiceMemoSettingsPanel({
  settings,
  onSettingsChange
}: VoiceMemoSettingsPanelProps) {
  const [open, setOpen] = useState(false);
  const [localSettings, setLocalSettings] = useState(settings);

  const handleChange = (key: keyof VoiceMemoSettings, value: boolean) => {
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
    onSettingsChange(newSettings);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Settings">
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Voice Memo Settings</DialogTitle>
          <DialogDescription>
            Configure automatic transcription and analysis behavior
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          {/* Auto Transcribe & Analyze (Master Toggle) */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="auto-transcribe">Auto Transcribe</Label>
                <p className="text-xs text-muted-foreground">
                  Automatically transcribe new voice memos
                </p>
              </div>
              <Switch
                id="auto-transcribe"
                checked={localSettings.autoTranscribe}
                onCheckedChange={(checked) => {
                  handleChange('autoTranscribe', checked);
                  if (!checked) {
                    handleChange('autoTranscribeOnUpload', false);
                    handleChange('autoTranscribeOnRecord', false);
                  }
                }}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="auto-analyze">Auto Analyze</Label>
                <p className="text-xs text-muted-foreground">
                  Automatically analyze transcribed memos
                </p>
              </div>
              <Switch
                id="auto-analyze"
                checked={localSettings.autoAnalyze}
                onCheckedChange={(checked) => {
                  handleChange('autoAnalyze', checked);
                  if (!checked) {
                    handleChange('autoAnalyzeOnUpload', false);
                    handleChange('autoAnalyzeOnRecord', false);
                  }
                }}
              />
            </div>
          </div>

          {/* Granular Controls */}
          {localSettings.autoTranscribe && (
            <div className="space-y-4 border-t border-border pt-4">
              <h3 className="text-sm font-semibold">When Uploading</h3>
              <div className="space-y-3 pl-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="transcribe-on-upload" className="text-sm">
                    Transcribe on upload
                  </Label>
                  <Switch
                    id="transcribe-on-upload"
                    checked={localSettings.autoTranscribeOnUpload}
                    onCheckedChange={(checked) => handleChange('autoTranscribeOnUpload', checked)}
                  />
                </div>
                {localSettings.autoTranscribeOnUpload && localSettings.autoAnalyze && (
                  <div className="flex items-center justify-between">
                    <Label htmlFor="analyze-on-upload" className="text-sm">
                      Analyze after transcription
                    </Label>
                    <Switch
                      id="analyze-on-upload"
                      checked={localSettings.autoAnalyzeOnUpload}
                      onCheckedChange={(checked) => handleChange('autoAnalyzeOnUpload', checked)}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {localSettings.autoTranscribe && (
            <div className="space-y-4 border-t border-border pt-4">
              <h3 className="text-sm font-semibold">When Recording</h3>
              <div className="space-y-3 pl-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="transcribe-on-record" className="text-sm">
                    Transcribe on record
                  </Label>
                  <Switch
                    id="transcribe-on-record"
                    checked={localSettings.autoTranscribeOnRecord}
                    onCheckedChange={(checked) => handleChange('autoTranscribeOnRecord', checked)}
                  />
                </div>
                {localSettings.autoTranscribeOnRecord && localSettings.autoAnalyze && (
                  <div className="flex items-center justify-between">
                    <Label htmlFor="analyze-on-record" className="text-sm">
                      Analyze after transcription
                    </Label>
                    <Switch
                      id="analyze-on-record"
                      checked={localSettings.autoAnalyzeOnRecord}
                      onCheckedChange={(checked) => handleChange('autoAnalyzeOnRecord', checked)}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Info Note */}
          <div className="border-t border-border pt-4">
            <p className="text-xs text-muted-foreground">
              When "Process All" is used, it will queue both transcription and analysis for all unprocessed memos, regardless of these settings.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
